// /work: every piece in two titled groups. Case studies, apps and plugins
// come first, two across, each on the stage it has on the homepage (a case
// study shows its gallery's first screen); interfaces and motion follow,
// three across, in their own artwork on the plain plate.
//
// A tile has at most one target, and then the whole tile is it: the article,
// the demo, the app or plugin page, or the image dialog. Apps and plugins also
// print their links in the meta line, so the way off-site is always named;
// a tile with two (the design-to-code plugins) opens the first.

import { built, showcases } from "./homepage";
import { caseStudies, codeProjects, motionProjects, uiProjects } from "./portfolioWork";

/**
 * @typedef {Object} WorkTile
 * @property {string} id
 * @property {string} title
 * @property {string} meta
 * @property {string} [href]                 // the whole tile's link
 * @property {boolean} [external]
 * @property {import("./homepage").Visual} [visual]   // a homepage stage
 * @property {import("./portfolioWork").MediaItem} [media]  // or the item's own artwork
 * @property {import("./portfolioWork").MediaItem[]} [gallery]  // opens the image dialog
 * @property {{label:string, short?:string, href:string, external?:boolean}[]} [links]
 */

const pick = (list, id) => {
  const item = list.find((entry) => entry.id === id);
  if (!item) throw new Error(`workIndex: no item with id "${id}"`);
  return item;
};

const firstScreen = ({ gallery }) => {
  const [slide] = gallery.slides;
  return {
    alt: slide.alt,
    ratio: gallery.ratio,
    backdrop: gallery.backdrop,
    layers: slide.layers,
  };
};

const caseStudyTile = (showcase) => {
  const item = caseStudies.find((entry) => entry.cta.href === showcase.href);
  return {
    id: item.id,
    title: item.title,
    meta: item.meta,
    href: item.cta.href,
    visual: firstScreen(showcase),
  };
};

const codeTile = (item, visual) => {
  const { links = [] } = item;
  const [primary] = links;
  return {
    id: item.id,
    title: item.title,
    meta: item.meta,
    visual,
    media: item.media,
    href: primary ? primary.href : undefined,
    external: primary ? Boolean(primary.external) : undefined,
    links,
  };
};

const visualTile = (item) => {
  let gallery = [];
  if (item.gallery && item.gallery.length) gallery = item.gallery;
  else if (item.media) gallery = [item.media];
  return {
    id: item.id,
    title: item.title,
    meta: item.meta,
    media: item.media,
    href: item.cta ? item.cta.href : undefined,
    external: item.cta ? Boolean(item.cta.external) : undefined,
    gallery: item.cta ? [] : gallery,
  };
};

/** @type {{id:string, title:string, columns:2|3, tiles:WorkTile[]}[]} */
export const workGroups = [
  {
    id: "case-studies-and-apps",
    title: "Case studies and apps",
    columns: 2,
    tiles: [
      ...showcases.map(caseStudyTile),
      codeTile(pick(codeProjects, "code-again"), pick(built, "again").visual),
      codeTile(pick(codeProjects, "code-tramat"), pick(built, "tramat").visual),
      codeTile(pick(codeProjects, "code-design-to-code"), pick(built, "design-to-code").visual),
      codeTile(pick(codeProjects, "code-halftone"), pick(built, "halftone").visual),
    ],
  },
  {
    id: "interfaces-and-motion",
    title: "Interfaces and motion",
    columns: 3,
    tiles: [...uiProjects.map(visualTile), ...motionProjects.map(visualTile)],
  },
];

const allTiles = workGroups.flatMap((group) => group.tiles);

/** The tile whose whole-tile link is `href`, for an article's related row. */
export const tileFor = (href) => allTiles.find((tile) => tile.href === href);

/** The homepage backdrop behind the piece at `href`, if it has a stage. */
export const stageBackdrop = (href) => tileFor(href)?.visual?.backdrop;

/** How many pieces /work holds, for the row that closes the homepage's work. */
export const workCount = allTiles.length;
